import { RefetchQueriesInclude, useLazyQuery } from "@apollo/client";
import { INCOMPLETE_PDF_MESSAGE } from "encamp-shared/src/pdf/messages";
import { gql } from "generated-graphql";
import {
  Document,
  DocumentAssociationsInput,
  DocumentInput2,
  UploadedDocumentQuery,
} from "generated-graphql/graphql";
import { useCallback, useMemo, useState } from "react";

import { uploadFileDirectly } from "../util/uploadFile";
import { useUpsertDocument } from "./useUpsertDocument";

const UPLOADED_DOCUMENT_QUERY = gql(`
  query UploadedDocument($id: ID!) {
    document(id: $id) {
      id
      tenantId
      documentType
      title
      fileExtension
      storageLink
      downloadLink
    }
  }
`);

const PDF_IS_LOADABLE_QUERY = gql(`
  query PdfIsLoadable($key: ID!, $tenantId: ID!) {
    pdfIsLoadable(key: $key, tenantId: $tenantId)
  }
`);

// Re-exported so the payment dialog can match the exact message it surfaces
// verbatim (rather than a fragile substring). PDF_UNVERIFIABLE_MESSAGE covers
// the case where the loadability check itself can't run (transient S3/network).
export { INCOMPLETE_PDF_MESSAGE };
export const PDF_UNVERIFIABLE_MESSAGE =
  "We couldn't verify this PDF right now. Please try attaching it again.";

/**
 * Hook for uploading a document and upserting it to the database
 * @returns
 */
export function useDocumentUpload(config?: {
  onSuccess?: (document: UploadedDocumentQuery["document"]) => void;
  onError?: (error: Error) => void;
  refetchQueries?: RefetchQueriesInclude;
  generateTitleOnCreate?: boolean;
}) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [document, setDocument] = useState<UploadedDocumentQuery["document"] | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const { upsertDocument } = useUpsertDocument({
    refetchQueries: config?.refetchQueries,
  });
  const [getDocument] = useLazyQuery(UPLOADED_DOCUMENT_QUERY);
  const [checkPdfLoadable] = useLazyQuery(PDF_IS_LOADABLE_QUERY);

  const uploadFile = useCallback(
    async ({
      file,
      documentData,
      currentSubmissionDocument,
      associations,
      validatePdf,
    }: {
      file?: File;
      documentData: Document | DocumentInput2;
      currentSubmissionDocument: boolean;
      associations?: DocumentAssociationsInput[];
      validatePdf?: boolean;
    }) => {
      setUploadComplete(false);
      setIsUploading(true);
      setDocument(null);
      setError(null);

      if (!documentData.tenantId) {
        throw new Error("Tenant ID is required");
      }

      try {
        let documentKey: string;

        if (file) {
          documentKey = await uploadFileDirectly(file, {
            tenantId: documentData.tenantId ?? "",
          });
        } else {
          if (!documentData.storageLink) {
            throw new Error("Storage link is required");
          }

          documentKey = documentData.storageLink;
        }

        if (validatePdf && file) {
          const { data: validation, error: validationError } = await checkPdfLoadable({
            variables: { key: documentKey, tenantId: documentData.tenantId },
          });
          if (validation?.pdfIsLoadable === false) {
            throw new Error(INCOMPLETE_PDF_MESSAGE);
          }
          // Fail closed: if the check couldn't return a definitive "loadable"
          // (query/network/S3 error or missing data), block rather than
          // silently attaching an unvalidated file.
          if (validationError || validation?.pdfIsLoadable !== true) {
            throw new Error(PDF_UNVERIFIABLE_MESSAGE);
          }
        }

        const documentId = await upsertDocument({
          documentData: {
            ...documentData,
            storageLink: documentKey,
          },
          generateTitleOnCreate: config?.generateTitleOnCreate,
          associations,
          currentSubmissionDocument,
        });

        if (!documentId) {
          throw new Error("Document ID not found");
        }
        const document = await getDocument({
          variables: { id: documentId },
        });

        if (!document.data?.document) {
          throw new Error("Document not found");
        }

        setDocument(document.data.document);
        setUploadComplete(true);
        config?.onSuccess?.(document.data.document);
      } catch (err) {
        setError(err as Error);
        config?.onError?.(err as Error);
      } finally {
        setIsUploading(false);
      }
    },
    [upsertDocument, getDocument, checkPdfLoadable, config]
  );

  return useMemo(
    () => ({
      isUploading,
      uploadComplete,
      document,
      error,
      uploadFile,
    }),
    [isUploading, uploadComplete, document, error, uploadFile]
  );
}

export function useUploadFiles() {
  return useCallback(
    async (documentsWithMaybeFile: { file?: File; documentData: DocumentInput2 }[]) => {
      if (!documentsWithMaybeFile.every((doc) => doc.documentData.tenantId)) {
        throw new Error("Tenant ID is required for all documents");
      }
      return await Promise.all(
        documentsWithMaybeFile.map(({ file, documentData }) =>
          file
            ? uploadFileDirectly(file, {
                tenantId: documentData.tenantId ?? "",
              })
            : Promise.resolve(documentData.storageLink)
        )
      );
    },
    []
  );
}
